'use client';

import React from 'react';
import type { ICellRendererParams } from 'ag-grid-community';
import { TopInsiderCompany } from '@benzinga/insider-trades-manager';

import styles from '../../insiderTrades.module.scss';
import { formatShareAxisLabel, formatSharesCompact } from './formatters';

export function totalVolumeForScale(row: TopInsiderCompany): number {
  const tv = row.total_volume;
  if (tv != null && Number.isFinite(Number(tv))) {
    return Math.max(0, Number(tv));
  }
  const { buy_volume, net_volume, sell_volume } = row;
  const hasSplit = buy_volume != null && sell_volume != null;
  if (hasSplit) {
    return Math.max(0, Number(buy_volume)) + Math.max(0, Number(sell_volume));
  }
  const netVal = net_volume ? Number(net_volume) : 0;
  return Math.abs(netVal);
}

export function getNetBarScaleMaxFromDataset(rows: readonly TopInsiderCompany[]): number {
  let m = 0;
  for (const row of rows) {
    m = Math.max(m, totalVolumeForScale(row));
  }
  return Math.max(m, 1);
}

export function getNetOnlyScaleMaxFromDataset(rows: readonly TopInsiderCompany[]): number {
  let m = 0;
  for (const row of rows) {
    const net = row.net_volume;
    if (net != null && Number.isFinite(Number(net))) {
      m = Math.max(m, Math.abs(Number(net)));
    }
  }
  return Math.max(m, 1);
}

const TICK_FRACTIONS = [0.25, 0.5, 0.75, 1] as const;

type NetBarProps = {
  data?: TopInsiderCompany | null;
  scaleMax?: number;
  value?: string | number;
};

export type NetBuySellBarCellRendererProps = Partial<ICellRendererParams<TopInsiderCompany>> & NetBarProps;

export function NetBuySellBarCellRenderer(props: NetBuySellBarCellRendererProps) {
  const row = props.data ?? undefined;
  if (!row) return null;

  const { buy_volume, net_volume, sell_volume } = row;
  const hasSplit = buy_volume != null && sell_volume != null;
  const netVal = net_volume ? Number(net_volume) : 0;
  const sellVal = hasSplit ? Number(sell_volume) : netVal < 0 ? Math.abs(netVal) : 0;
  const buyVal = hasSplit ? Number(buy_volume) : netVal > 0 ? netVal : 0;

  const scaleMax = props.scaleMax ?? Math.max(totalVolumeForScale(row), Math.max(buyVal, sellVal, 1));

  const sellSpanPct = Math.min(50, (sellVal / scaleMax) * 50);
  const buySpanPct = Math.min(50, (buyVal / scaleMax) * 50);
  const leftPct = 50 - sellSpanPct;
  const midPct = 50;

  return (
    <div className={styles['net-bar-wrap']}>
      <div
        className={styles['net-bar-track']}
        role="img"
        aria-label={`Net ${formatSharesCompact(net_volume != null ? Number(net_volume) : 0)} shares`}
      >
        <div
          className={styles['net-bar-segment']}
          style={{
            position: 'absolute',
            left: `${leftPct}%`,
            width: `${sellSpanPct}%`,
            height: '100%',
            backgroundColor: 'var(--sell-color, #FF4050)',
            borderRadius: '4px 0 0 4px',
          }}
        />
        <div
          className={styles['net-bar-segment']}
          style={{
            position: 'absolute',
            left: `${midPct}%`,
            width: `${buySpanPct}%`,
            height: '100%',
            backgroundColor: 'var(--buy-color, #30BF60)',
            borderRadius: '0 3px 3px 0',
          }}
        />
        {TICK_FRACTIONS.map(f => {
          const amt = f * scaleMax;
          const left = f === 1 ? 1 : 50 * (1 - f);
          return (
            <span
              key={`sell-${f}`}
              className={styles['net-bar-dot']}
              title={`${formatShareAxisLabel(amt)} sold`}
              style={{ left: `${left}%` }}
            />
          );
        })}
        <span className={styles['net-bar-dot']} title="0" style={{ left: '50%', opacity: 0.5 }} />
        {TICK_FRACTIONS.map(f => {
          const amt = f * scaleMax;
          const left = f === 1 ? 99 : 50 + 50 * f;
          return (
            <span
              key={`buy-${f}`}
              className={styles['net-bar-dot']}
              title={`${formatShareAxisLabel(amt)} bought`}
              style={{ left: `${left}%` }}
            />
          );
        })}
      </div>
    </div>
  );
}

export function NetVolumeGraphLegend({ scaleMax }: { scaleMax: number }) {
  const descending = [...TICK_FRACTIONS].reverse().map(f => formatShareAxisLabel(f * scaleMax));
  const ascending = [...descending].reverse();

  return (
    <tr>
      <td>
        <span className="text-[#99AECC] mt-2">Total $ Volume</span>
      </td>
      <td className="relative">
        <div className="w-full relative flex-1 flex-grow flex items-center justify-between">
          {descending.map((label, i) => (
            <span key={`sell-${i}`} className="text-red-500">
              -${label}
            </span>
          ))}
          <span className="text-[#5b7292]">0</span>
          {ascending.map((label, i) => (
            <span key={`buy-${i}`} className="text-green-500">
              +${label}
            </span>
          ))}
        </div>
        <div className="absolute -bottom-4 px-2 left-0 w-full h-full flex flex-row items-center justify-between">
          <span className="text-[#5b7292]">SELL</span>
          <span className="text-[#5b7292]">BUY</span>
        </div>
      </td>
      <td className="text-right max-w-[120px]">
        <span className="text-[#99AECC] mt-2">Total $ Volume</span>
      </td>
    </tr>
  );
}
